import { useState } from "react";
import useSWR from "swr";

import { useAxios } from "../http-client/AxiosContext";
import type { NotificationEvent, NotificationFeed } from "../types/Notification";
import { NOTIF_DISMISS_KEY, feedHasNewSince } from "../components/notifications/feedDismiss";

const readWatermark = (): string | null =>
  typeof window !== "undefined" ? window.sessionStorage.getItem(NOTIF_DISMISS_KEY) : null;

// Notification feed + unread state, shared by whatever surfaces it. Polls every
// 60s and revalidates on focus. `markSeen` optimistically zeroes the unread
// count and persists the cursor server-side (re-fetches on failure).
//
// `showFeed`/`dismissFeed` drive the dismissable feed island: dismiss persists
// a "dismissed-through" watermark in sessionStorage so it survives reopen,
// navigation/remount, and other UserControls instances, yet reappears when a
// genuinely newer notification arrives (see feedDismiss.ts).
export const useNotifications = () => {
  const { ax } = useAxios();

  const { data, mutate } = useSWR<NotificationFeed>(
    ax ? "/notifications/" : null,
    (url: string) => ax.get(url).then((res) => res.data),
    { refreshInterval: 60_000, revalidateOnFocus: true, shouldRetryOnError: false },
  );

  const events: NotificationEvent[] = data?.events ?? [];
  const unseen = data?.unseen_count ?? 0;

  const [dismissedThrough, setDismissedThrough] = useState<string | null>(readWatermark);
  const showFeed = feedHasNewSince(events, dismissedThrough);

  const dismissFeed = () => {
    const newest = events[0]?.at ?? null; // build_notifications returns newest-first
    if (!newest) return;
    if (typeof window !== "undefined")
      window.sessionStorage.setItem(NOTIF_DISMISS_KEY, newest);
    setDismissedThrough(newest);
  };

  const markSeen = () => {
    if (unseen === 0) return;
    mutate(
      (current) => (current ? { ...current, unseen_count: 0 } : current),
      { revalidate: false },
    );
    ax.post("/notifications/seen/").catch(() => mutate());
  };

  return { events, unseen, markSeen, showFeed, dismissFeed };
};
